/**
 * Tago's single Mapbox Standard contract.
 *
 * The previous maps each selected an old light/dark "classic" style. Aside
 * from making the visual language drift, that made every screen decide for
 * itself whether it could afford 3D and how much map detail to show. Standard
 * gives us one maintained basemap with a predictable 3D/light model; this
 * module is the only place that chooses its configuration.
 *
 * Discovery and operational surfaces all use vector Standard. Standard
 * Satellite is a separate imagery product; it is deliberately not part of
 * Tago's basemap contract because the product needs legible roads, buildings,
 * landmarks, and routes rather than aerial texture.
 *
 * Mapbox's native Standard SDK exposes `default`, `faded`, and `monochrome`
 * themes. Tago uses the colorful `default` theme as its vivid baseline, then
 * keeps the app's own route and marker accents above it. The light preset is
 * the source of truth for day/night lighting.
 */

export const MAPBOX_STANDARD_STYLE_URL = 'mapbox://styles/mapbox/standard'

export type TagoMapPresentation = 'discovery' | 'route' | 'picker'

/**
 * How a compact inventory map chooses its first camera frame. This is a
 * product framing decision, not a restriction on the inventory: the user can
 * still pan, search, filter, or explicitly fit all results.
 */
export type MapInitialFrame = 'default-department' | 'results'

/** A camera frame expressed as northeast/southwest longitude-latitude pairs. */
export type MapboxCameraBounds = {
  ne: [number, number]
  sw: [number, number]
}

type MapAppearance = 'light' | 'dark'

type SharedStandardConfig = {
  lightPreset: 'day' | 'night'
  showPointOfInterestLabels: boolean
  showTransitLabels: boolean
  showPlaceLabels: boolean
  showRoadLabels: boolean
  showPedestrianRoads: boolean
  /** Preserve Mapbox Standard's native, category-coloured POI treatment. */
  colorModePointOfInterestLabels: 'default'
}

/**
 * A restrained vivid palette for the daytime storefront. These are supported
 * Standard configuration properties, not a second style: Mapbox continues to
 * own the 3D geometry, labels, lighting, and cartographic updates.
 */
type VividDayColors = {
  colorAdminBoundaries: string
  colorCommercial: string
  colorEducation: string
  colorMedical: string
  colorIndustrial: string
  colorGreenspace: string
  colorWater: string
  colorLand: string
  colorBuildings: string
  colorRoads: string
  colorMotorways: string
  colorTrunks: string
}

/** Configuration supported by the vector Mapbox Standard basemap. */
export type TagoMapboxStandardConfig = SharedStandardConfig & {
  theme: 'default'
  show3dObjects: boolean
  show3dBuildings: boolean
  show3dFacades: boolean
  show3dLandmarks: boolean
  show3dTrees: boolean
  colorAdminBoundaries?: string
  colorCommercial?: string
  colorEducation?: string
  colorMedical?: string
  colorIndustrial?: string
  colorGreenspace?: string
  colorWater?: string
  colorLand?: string
  colorBuildings?: string
  colorRoads?: string
  colorMotorways?: string
  colorTrunks?: string
}

export type TagoMapboxStandardStyleConfig = TagoMapboxStandardConfig

/** Every native map uses the same vector Mapbox Standard 3D basemap. */
export function mapboxStandardStyleURL(presentation: TagoMapPresentation): string {
  void presentation
  return MAPBOX_STANDARD_STYLE_URL
}

/**
 * Returns a stable, purpose-specific Standard configuration.
 *
 * Discovery and route maps are an editorial surface, so they retain the 3D
 * environment. A location picker is a precision task: removing building
 * extrusion there keeps the centre pin unambiguous without making discovery
 * maps less expressive. Its lighting deliberately follows the surrounding
 * appearance: daylight stays readable in light mode, and nighttime lighting
 * prevents a bright geographic canvas from flashing against dark app chrome.
 */
export function mapboxStandardConfig(
  presentation: TagoMapPresentation,
  appearance: MapAppearance,
): TagoMapboxStandardStyleConfig {
  const richScene = presentation !== 'picker'
  const vividDayColors: VividDayColors = {
    colorAdminBoundaries: '#f26b9b',
    colorCommercial: '#ffe4d6',
    colorEducation: '#fff0b8',
    colorMedical: '#ffd7e0',
    colorIndustrial: '#e8e4f0',
    colorGreenspace: '#a9e58e',
    colorWater: '#70d2f4',
    colorLand: '#f6f1ed',
    colorBuildings: '#f1e9de',
    colorRoads: '#ffffff',
    colorMotorways: '#9eafd2',
    colorTrunks: '#a8b1d0',
  }
  const shared: SharedStandardConfig = {
    // Mapbox Standard's official presets control the light, shadows and colour
    // temperature across the 3D scene. This is not a hand-made palette, so it
    // continues to receive Mapbox's cartographic updates.
    lightPreset: appearance === 'dark' ? 'night' : 'day',
    showPointOfInterestLabels: true,
    // Transit data is not an actionable inventory signal in Tago's current
    // market. Leaving it out reduces visual noise before a person has picked
    // a vehicle or route.
    showTransitLabels: false,
    showPlaceLabels: true,
    showRoadLabels: true,
    showPedestrianRoads: false,
    colorModePointOfInterestLabels: 'default' as const,
  }

  if (presentation === 'discovery') {
    return {
      ...shared,
      // `default` is Mapbox's color-rich Standard theme. It is the supported
      // native equivalent of the vivid visual direction without introducing a
      // private LUT asset or a second custom style to maintain.
      theme: 'default',
      show3dObjects: true,
      show3dBuildings: true,
      show3dFacades: true,
      show3dLandmarks: true,
      show3dTrees: true,
      ...(appearance === 'light' ? vividDayColors : {}),
    }
  }

  return {
    ...shared,
    theme: 'default',
    show3dObjects: richScene,
    show3dBuildings: richScene,
    show3dFacades: richScene,
    show3dLandmarks: richScene,
    // Keep trees on for rich scenes; the precision picker remains deliberately
    // flat and cheap while the storefront and route views get the full depth
    // treatment users expect from Standard's 3D basemap.
    show3dTrees: richScene,
    ...(appearance === 'light' && richScene ? vividDayColors : {}),
  }
}

/**
 * Full-screen discovery is a scene, not a country overview. The previous
 * 30-degree / nationwide frame technically enabled 3D, but reduced every
 * building to a pixel. These values put the first full-map frame inside the
 * city, where Mapbox Standard's façade light, terrain and depth are visible.
 * A person can still press "fit results" to inspect the whole inventory.
 */
export const MAPBOX_DISCOVERY_PITCH = 55
export const MAPBOX_DISCOVERY_BEARING = -24
export const MAPBOX_DISCOVERY_HERO_ZOOM = 15.7
export const MAPBOX_ROUTE_PITCH = 34

/**
 * Tago's initial storefront frame for El Salvador. It keeps the San Salvador
 * department and metro area legible in the compact card instead of zooming out
 * to the country's full inventory extent on first paint. It is intentionally a
 * stable camera rectangle rather than a legal administrative boundary.
 */
export const MAPBOX_DEFAULT_SV_DEPARTMENT_FRAME: MapboxCameraBounds = {
  // Keep the frame close to the department's urban center so the compact
  // card reads as a local storefront, not as a country atlas.
  ne: [-89.06, 13.795],
  sw: [-89.3, 13.645],
}
